import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { HandHeart, Check, Hourglass, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

export default function Blessing() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/blessing/${token}`).then((r) => { setB(r.data); if (r.data.status === "active") setDone(true); })
      .catch(() => setErr("This blessing could not be found."));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/public/blessing/${token}/accept`, { covenant_agreed: agreed });
      setDone(true);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not receive the blessing."); }
    finally { setBusy(false); }
  };

  if (err) return <Centered><p className="text-textMuted">{err}</p></Centered>;
  if (!b) return <Centered><Hourglass className="w-6 h-6 text-gold animate-glow"/></Centered>;

  const needsCovenant = (b.step_ups || []).includes("covenant") && b.covenant_text;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <Seo title="A blessing for you" />
      <div className="max-w-lg w-full panel p-8 text-center" data-testid="blessing-page">
        <HandHeart className="w-10 h-10 text-gold mx-auto mb-5" />
        {done ? (
          <>
            <div className="overline mb-3 text-gold">// RECEIVED</div>
            <h1 className="font-display text-4xl text-cream mb-4">The blessing is active.</h1>
            <p className="text-textMuted mb-6">A season of access has been opened for you. Step into it fully.</p>
            <Link to="/dashboard" className="btn-gold inline-flex" data-testid="blessing-enter"><Sparkles className="w-4 h-4"/> Enter the realm</Link>
          </>
        ) : (
          <>
            <div className="overline mb-3 text-gold">// A BLESSING HAS BEEN SET ASIDE FOR YOU</div>
            <h1 className="font-display text-4xl text-cream mb-4">{b.member_name ? `${b.member_name},` : "You are seen."}</h1>
            {b.message && <p className="text-cream/90 leading-relaxed mb-5 whitespace-pre-wrap">{b.message}</p>}
            <p className="text-sm text-textDim mb-6">{b.duration_days} days of {b.blessing_type === "full" ? "Sovereign" : "Foundational"} access — a gift, freely given.</p>

            {needsCovenant && (
              <label className="flex items-start gap-3 text-left border border-borderGold p-4 mb-6 cursor-pointer" data-testid="covenant-check">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 accent-[#C9A23B]"/>
                <span className="text-sm text-cream/90 italic">{b.covenant_text}</span>
              </label>
            )}

            <button data-testid="blessing-accept" disabled={busy || (needsCovenant && !agreed)} onClick={accept}
              className="btn-gold inline-flex disabled:opacity-40">
              <Check className="w-4 h-4"/> {busy ? "Receiving…" : "Receive this blessing"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center px-6">{children}</div>;
}
